import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import styled from 'styled-components';

import { truncateText } from './utils/mixins';
import { useSidebarCtx } from './SidebarContext';

// ============================================================
//     OVERLAY SCROLLBAR
// ============================================================
/** How long the thumb stays visible after the last scroll/hover event, in ms. Matches FE2.0's `ScrollArea` default `scrollHideDelay`. */
const SCROLL_HIDE_DELAY_MS = 600;

/**
 * Track/thumb geometry, shared between the layout (`StyledScrollTrack`/
 * `StyledScrollThumb`) and the `updateThumb` math below so the thumb's travel
 * range always matches the track's visible bounds instead of the viewport's
 * full height.
 */
const SCROLLBAR_INSET = 2;
const SCROLLBAR_WIDTH = 8;
const SCROLLBAR_TOP = 2;
const SCROLLBAR_BOTTOM = 2;

/**
 * Tracks scroll position/content size for a custom overlay scrollbar thumb.
 *
 * Stand-in for the Frontend-Platform `ScrollArea` atom (no component-library
 * equivalent exists): unlike a native `overflow-y: auto` scrollbar, this never
 * reserves layout width, so it doesn't eat into the collapsed sidebar rail's
 * already-narrow content width and cause labels to truncate early.
 */
const useOverlayScrollbar = () => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [thumb, setThumb] = useState<{ height: number; top: number } | null>(
    null,
  );
  const [isVisible, setIsVisible] = useState(false);

  const updateThumb = useCallback(() => {
    const viewport = viewportRef.current;
    /* v8 ignore next -- defensive: ref is always attached by the time this runs */
    if (!viewport) return;

    const { clientHeight, scrollHeight, scrollTop } = viewport;
    if (scrollHeight <= clientHeight) {
      setThumb(null);
      return;
    }

    // The thumb travels within the track's inset bounds, not the full
    // viewport height, so it never overshoots the track's rounded ends.
    const trackLength = clientHeight - SCROLLBAR_TOP - SCROLLBAR_BOTTOM;
    const height = (clientHeight / scrollHeight) * trackLength;
    const top = SCROLLBAR_TOP
      + ((scrollTop / (scrollHeight - clientHeight)) * (trackLength - height));
    setThumb({ height, top });
  }, []);

  const revealThumb = useCallback(() => {
    setIsVisible(true);
    clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(
      () => setIsVisible(false),
      SCROLL_HIDE_DELAY_MS,
    );
  }, []);

  const handleScroll = useCallback(() => {
    updateThumb();
    revealThumb();
  }, [updateThumb, revealThumb]);

  useEffect(() => {
    const viewport = viewportRef.current;
    /* v8 ignore next -- defensive: ref is always attached by the time this runs */
    if (!viewport) return undefined;

    updateThumb();

    const resizeObserver = new ResizeObserver(updateThumb);
    resizeObserver.observe(viewport);
    if (viewport.firstElementChild) {
      resizeObserver.observe(viewport.firstElementChild);
    }

    return () => {
      resizeObserver.disconnect();
      clearTimeout(hideTimeoutRef.current);
    };
  }, [updateThumb]);

  return {
    handleScroll,
    isVisible,
    onMouseEnter: () => setIsVisible(true),
    onMouseLeave: () => setIsVisible(false),
    thumb,
    viewportRef,
  };
};

// ============================================================
//     TYPES
// ============================================================
interface ContentProps extends React.ComponentPropsWithoutRef<'div'> {}

// ============================================================
//     STYLES
// ============================================================
const StyledContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--cl-space-4)',
  flex: '0 0 auto',

  width: '100%',
  paddingBlock: 'var(--cl-space-1)',
  paddingInline: 'var(--cl-space-4)',

  ...truncateText,
  overflow: 'clip',

  transitionProperty: 'width',
  transitionDuration: 'var(--cl-duration-300)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',
});

const StyledBody = styled(StyledContent)({
  flex: '1 1 auto',
  minHeight: '100%',
  paddingBlock: 'var(--cl-space-3)',
});

const StyledScrollWrapper = styled.div({
  position: 'relative',
  flex: '1 1 auto',
  minHeight: 0,
});

const StyledScrollViewport = styled.div({
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',

  // Native scrollbar is hidden entirely — the sibling `StyledScrollThumb` below
  // renders the visible affordance instead, without reserving any layout width.
  scrollbarWidth: 'none',

  '&::-webkit-scrollbar': {
    display: 'none',
  },
});

const StyledScrollTrack = styled.div({
  position: 'absolute',
  top: SCROLLBAR_TOP,
  bottom: SCROLLBAR_BOTTOM,
  right: SCROLLBAR_INSET,
  width: SCROLLBAR_WIDTH,

  background: 'var(--cl-gray-300)',
  borderRadius: '4px',
  pointerEvents: 'none',

  opacity: 0,
  transitionProperty: 'opacity',
  transitionDuration: 'var(--cl-duration-200)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',

  '&[data-visible="true"]': {
    opacity: 1,
  },
});

const StyledScrollThumb = styled.div({
  position: 'absolute',
  top: SCROLLBAR_TOP,
  right: SCROLLBAR_INSET,
  width: SCROLLBAR_WIDTH,

  background: 'var(--cl-gray-500)',
  borderRadius: '4px',
  pointerEvents: 'none',

  opacity: 0,
  transitionProperty: 'opacity',
  transitionDuration: 'var(--cl-duration-200)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',

  '&[data-visible="true"]': {
    opacity: 1,
  },
});

// ============================================================
//     COMPONENTS
// ============================================================
/**
 * Fixed top section of the sidebar for persistent header content.
 *
 * Typically used for logos, account switchers, or primary navigation controls that should remain visible regardless of scroll position.
 */
export const Header = React.forwardRef<HTMLDivElement, ContentProps>(
  (props, forwardedRef) => {
    const { isExpanded } = useSidebarCtx();
    return (
      <StyledContent
        {...props}
        data-component="Sidebar.Header"
        data-expanded={isExpanded}
        ref={forwardedRef}
      />
    );
  },
);

Header.displayName = 'Sidebar.Header' as const;

/**
 * Fixed bottom section of the sidebar for persistent footer content.
 *
 * Typically used for user account controls, settings links, or secondary actions that should remain visible regardless of scroll position.
 */
export const Footer = React.forwardRef<HTMLDivElement, ContentProps>(
  (props, forwardedRef) => {
    const { isExpanded } = useSidebarCtx();
    return (
      <StyledContent
        {...props}
        data-component="Sidebar.Footer"
        data-expanded={isExpanded}
        ref={forwardedRef}
      />
    );
  },
);

Footer.displayName = 'Sidebar.Footer' as const;

/**
 * Scrollable main content area of the sidebar.
 *
 * Wraps content in a vertical scroll container.
 * Use this for the primary navigation content that may overflow the viewport.
 */
export const Body = React.forwardRef<HTMLDivElement, ContentProps>(
  (props, forwardedRef) => {
    const { children, ...restProps } = props;
    const { isExpanded } = useSidebarCtx();
    const {
      handleScroll,
      isVisible,
      onMouseEnter,
      onMouseLeave,
      thumb,
      viewportRef,
    } = useOverlayScrollbar();

    return (
      <StyledScrollWrapper
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <StyledScrollViewport onScroll={handleScroll} ref={viewportRef}>
          <StyledBody
            {...restProps}
            data-component="Sidebar.Body"
            data-expanded={isExpanded}
            ref={forwardedRef}
          >
            {children}
          </StyledBody>
        </StyledScrollViewport>
        {!!thumb && (
          <>
            <StyledScrollTrack data-visible={isVisible} />
            <StyledScrollThumb
              data-visible={isVisible}
              style={{ height: thumb.height, top: thumb.top }}
            />
          </>
        )}
      </StyledScrollWrapper>
    );
  },
);

Body.displayName = 'Sidebar.Body' as const;
